import { useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useCreateSbaCalendar } from '@/hooks/useSbaCalendars'
import { useTemplateSummaries } from '@/hooks/useCltTemplates'

interface CreateSbaCalendarDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const NO_TEMPLATE = '__none__'

const schema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(200),
  year: z.coerce
    .number()
    .int('Year must be a whole number')
    .min(2000, 'Year must be ≥ 2000')
    .max(2100, 'Year must be ≤ 2100'),
  description: z.string().max(1000).optional(),
  default_logic_template_id: z.string().optional(),
})

type FormValues = z.infer<typeof schema>

const defaultValues = (): FormValues => ({
  name: '',
  year: new Date().getFullYear(),
  description: '',
  default_logic_template_id: NO_TEMPLATE,
})

export function CreateSbaCalendarDialog({
  open,
  onOpenChange,
}: CreateSbaCalendarDialogProps) {
  const createCalendar = useCreateSbaCalendar()
  const { data: templates } = useTemplateSummaries()

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: defaultValues(),
  })

  const hasInitializedRef = useRef(false)

  useEffect(() => {
    if (!open) {
      reset(defaultValues())
      hasInitializedRef.current = false
      return
    }
    if (!hasInitializedRef.current && templates && templates.length > 0) {
      const defaultTemplate = templates.find(t => t.isDefault)
      if (defaultTemplate) {
        setValue('default_logic_template_id', defaultTemplate.id, {
          shouldDirty: false,
        })
      }
      hasInitializedRef.current = true
    }
  }, [open, templates, setValue, reset])

  const selectedTemplate = watch('default_logic_template_id') ?? NO_TEMPLATE

  const onSubmit = async (data: FormValues) => {
    await createCalendar.mutateAsync({
      name: data.name,
      year: data.year,
      description: data.description?.trim() ? data.description.trim() : null,
      default_logic_template_id:
        data.default_logic_template_id &&
        data.default_logic_template_id !== NO_TEMPLATE
          ? data.default_logic_template_id
          : null,
    })
    onOpenChange(false)
  }

  return (
    <Dialog
      open={open}
      onOpenChange={createCalendar.isPending ? undefined : onOpenChange}
    >
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Create Calendar</DialogTitle>
          <DialogDescription>
            Seeds an SBA calendar and its events for the year. The compute task
            runs in the background.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              placeholder="e.g. SBA 2026"
              autoComplete="off"
              {...register('name')}
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="year">Year</Label>
            <Input
              id="year"
              type="number"
              min={2000}
              max={2100}
              {...register('year')}
            />
            {errors.year && (
              <p className="text-sm text-destructive">{errors.year.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="default_logic_template_id">
              Default Calendar Logic Template
            </Label>
            <Select
              value={selectedTemplate}
              onValueChange={value =>
                setValue('default_logic_template_id', value, {
                  shouldDirty: true,
                })
              }
            >
              <SelectTrigger id="default_logic_template_id">
                <SelectValue placeholder="Select a template" />
              </SelectTrigger>
              <SelectContent>
                {templates?.map(t => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name}
                    {t.isDefault ? ' · default' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              placeholder="Optional description..."
              {...register('description')}
            />
            {errors.description && (
              <p className="text-sm text-destructive">
                {errors.description.message}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={createCalendar.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createCalendar.isPending}>
              {createCalendar.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Create'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
